import React from "react";
import { Link } from "react-router-dom";
import { Phone, Printer, Mail } from "lucide-react";
import { SITE } from "../../lib/siteData";
import SocialIcons from "./SocialIcons";

const FOOTER_NAV = [
  { label: "Home", href: "/#top" },
  { label: "Why Us", href: "/#why" },
  { label: "Team", href: "/#team" },
  { label: "Testimonials", href: "/#testimonials" },
  { label: "Get A Quote", href: "/#quote" },
];

export default function SiteFooter() {
  return (
    <footer className="bg-[#011233] text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          {/* Brand — left of Menu */}
          <div className="md:col-span-4">
            <img
              src="/knight-logo-header.png"
              alt={SITE.brand}
              className="h-[124px] w-auto"
              style={{ objectFit: "contain" }}
              data-testid="footer-logo"
            />
            <p className="mt-5 text-[14px] text-white max-w-md leading-relaxed">
              Independent insurance agency representing multiple carriers — so you get
              comprehensive coverage at competitive pricing, with personal service.
            </p>
            <SocialIcons
              size={16}
              className="mt-6"
              linkClassName="text-white hover:text-[#B5BAC2] transition-colors p-2 rounded-full border border-white/15"
            />
          </div>

          {/* Menu */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/70 mb-5 font-semibold">
              Menu
            </h4>
            <ul className="space-y-2.5 text-[14px] text-white">
              {FOOTER_NAV.map((n) => (
                <li key={n.label}>
                  <a
                    href={n.href}
                    className="hover:text-[#B5BAC2] transition-colors"
                    data-testid={`footer-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {n.label}
                  </a>
                </li>
              ))}
              <li>
                <Link to="/agent/login" className="hover:text-[#B5BAC2] transition-colors">
                  Client Portal
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/70 mb-5 font-semibold">
              Services
            </h4>
            <ul className="space-y-2.5 text-[14px] text-white">
              {SITE.services.map((s) =>
                s.href && s.href.startsWith("/") && !s.href.startsWith("/#") ? (
                  <li key={s.slug}>
                    <Link to={s.href} className="hover:text-[#B5BAC2] transition-colors">
                      {s.title}
                    </Link>
                  </li>
                ) : (
                  <li key={s.slug}>
                    <a href={s.href || `#service-${s.slug}`} className="hover:text-[#B5BAC2] transition-colors">
                      {s.title}
                    </a>
                  </li>
                )
              )}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <h4 className="text-xs uppercase tracking-[0.2em] text-white/70 mb-5 font-semibold">
              Contact
            </h4>
            <ul className="space-y-4 text-[14px]" data-testid="footer-contact">
              <FootRow icon={Phone} label="Toll-free">
                <a className="hover:text-[#B5BAC2] transition-colors" href={`tel:${SITE.toll_free_tel}`}>
                  {SITE.toll_free}
                </a>
              </FootRow>
              <FootRow icon={Phone} label="Office">
                <a className="hover:text-[#B5BAC2] transition-colors" href={`tel:${SITE.phone.replace(/[^\d]/g, "")}`}>
                  1 {SITE.phone}
                </a>
              </FootRow>
              <FootRow icon={Printer} label="Fax">
                {SITE.fax}
              </FootRow>
              <FootRow icon={Mail} label="Email">
                <a className="hover:text-[#B5BAC2] transition-colors break-all" href={`mailto:${SITE.email}`} data-testid="footer-email">
                  {SITE.email}
                </a>
              </FootRow>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[13px] text-white/85">
          <span>© {new Date().getFullYear()} {SITE.brand}. All rights reserved.</span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-[#B5BAC2] transition-colors" data-testid="footer-privacy">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-[#B5BAC2] transition-colors" data-testid="footer-terms">
              Terms &amp; Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FootRow({ icon: Icon, label, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/8 text-[#B5BAC2] flex-shrink-0">
        <Icon size={14} />
      </span>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.2em] text-white/60 font-semibold">{label}</p>
        <p className="text-white mt-0.5 leading-relaxed">{children}</p>
      </div>
    </li>
  );
}
